"use client";

import { useCallback } from "react";
import type { HyperliquidAbstractionMode, HyperliquidApproveResult, UserSetAbstractionMessage } from "@cede/utils";
import {
  HYPERLIQUID_APPROVAL_DOMAIN,
  HYPERLIQUID_CHAIN_MAINNET,
  HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
  HYPERLIQUID_USER_SET_ABSTRACTION_TYPES,
  USER_SET_ABSTRACTION_PRIMARY,
  postHyperliquidExchange,
  postHyperliquidInfo,
  splitSignature,
} from "@cede/utils";
import { type HyperliquidAbstractionModeResult, useHyperliquidAbstractionMode } from "./use-abstraction-mode";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Parameters for a typed-data signature request, structurally compatible with
 * wagmi's `signTypedDataAsync`. Kept local so the package does not depend on wagmi.
 */
export interface SignTypedDataParams {
  domain: typeof HYPERLIQUID_APPROVAL_DOMAIN;
  types: typeof HYPERLIQUID_USER_SET_ABSTRACTION_TYPES;
  primaryType: typeof USER_SET_ABSTRACTION_PRIMARY;
  message: UserSetAbstractionMessage;
}

export interface UseHyperliquidAbstractionModeMutationParams {
  userAddress: string | undefined;
  enabled?: boolean;
  /** Signs the EIP-712 `userSetAbstraction` payload (injected by the consumer; e.g. wagmi-backed). */
  signTypedDataAsync: (params: SignTypedDataParams) => Promise<`0x${string}`>;
  /** Side effect fired once when unified account is freshly enabled (e.g. success snackbar). */
  onEnableSuccess?: () => void;
  /** Side effect fired when enabling fails (e.g. Sentry capture + error snackbar). User-rejection filtering lives here. */
  onEnableError?: (error: Error) => void;
}

export interface HyperliquidAbstractionModeMutation extends HyperliquidAbstractionModeResult {
  /** Mutation to enable unified account mode (EIP-712 signature required) */
  enableUnifiedAccount: () => Promise<void>;
  /** Whether the enable mutation is in progress */
  isEnabling: boolean;
}

type EnableUnifiedAccountOutcome = {
  mode: "enabled" | "already-enabled" | "confirmed-after-duplicate";
  result: Extract<HyperliquidApproveResult, { status: "ok" }>;
};

const unifiedAccountEnablePromises = new Map<string, Promise<EnableUnifiedAccountOutcome>>();
const lastUnifiedAccountNonceByAddress = new Map<string, number>();

function getNextUnifiedAccountNonce(addressKey: string) {
  const now = Date.now();
  const previousNonce = lastUnifiedAccountNonceByAddress.get(addressKey) ?? 0;
  const nextNonce = Math.max(now, previousNonce + 1);
  lastUnifiedAccountNonceByAddress.set(addressKey, nextNonce);
  return nextNonce;
}

function isUnifiedMode(abstractionMode: HyperliquidAbstractionMode | null) {
  return abstractionMode === "unifiedAccount" || abstractionMode === "portfolioMargin";
}

/**
 * Read + mutate hook for Hyperliquid account abstraction mode.
 *
 * Composes the read-only `useHyperliquidAbstractionMode` and adds the EIP-712
 * `userSetAbstraction` mutation to switch a user to `unifiedAccount`.
 *
 * Hub-specific concerns (wallet-connection-aware signing, Sentry, snackbars,
 * user-rejection filtering) are injected so the package stays framework-agnostic.
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/trading/account-abstraction-modes
 */
export function useHyperliquidAbstractionModeMutation({
  userAddress,
  enabled = true,
  signTypedDataAsync,
  onEnableSuccess,
  onEnableError,
}: UseHyperliquidAbstractionModeMutationParams): HyperliquidAbstractionModeMutation {
  const readState = useHyperliquidAbstractionMode(userAddress, enabled);
  const queryClient = useQueryClient();
  const addressKey = userAddress?.toLowerCase();

  const queryKey = ["hyperliquid-account-mode", addressKey] as const;

  const checkAccountMode = useCallback(async (): Promise<HyperliquidAbstractionMode | null> => {
    if (!addressKey) {
      return null;
    }

    return await postHyperliquidInfo<HyperliquidAbstractionMode>({
      type: "userAbstraction",
      user: addressKey,
    });
  }, [addressKey]);

  // Sign and submit userSetAbstraction to switch to unified account mode
  const { mutateAsync: enableUnifiedAccountMutation, isPending: isEnabling } = useMutation({
    mutationFn: async (): Promise<EnableUnifiedAccountOutcome> => {
      if (!addressKey) {
        throw new Error("Wallet not connected");
      }

      const currentMode = await checkAccountMode();
      if (isUnifiedMode(currentMode)) {
        queryClient.setQueryData(queryKey, currentMode);
        return {
          mode: "already-enabled",
          result: { status: "ok", response: { type: "default" } },
        };
      }

      const nonce = getNextUnifiedAccountNonce(addressKey);

      const message: UserSetAbstractionMessage = {
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        user: addressKey as `0x${string}`,
        abstraction: "unifiedAccount",
        nonce: BigInt(nonce),
      };

      const signature = await signTypedDataAsync({
        domain: HYPERLIQUID_APPROVAL_DOMAIN,
        types: HYPERLIQUID_USER_SET_ABSTRACTION_TYPES,
        primaryType: USER_SET_ABSTRACTION_PRIMARY,
        message,
      });

      const sigComponents = splitSignature(signature);

      const action = {
        type: "userSetAbstraction",
        hyperliquidChain: HYPERLIQUID_CHAIN_MAINNET,
        signatureChainId: HYPERLIQUID_DEFAULT_CHAIN_ID_HEX,
        user: addressKey,
        abstraction: "unifiedAccount",
        nonce,
      };

      const result = await postHyperliquidExchange<HyperliquidApproveResult>({
        action,
        nonce,
        signature: sigComponents,
      });

      if (result.status === "err") {
        const errorMessage = typeof result.response === "string" ? result.response : "Failed to enable unified account";
        const isDuplicateNonce = errorMessage.toLowerCase().includes("duplicate nonce");

        if (isDuplicateNonce) {
          const confirmedMode = await checkAccountMode();
          if (isUnifiedMode(confirmedMode)) {
            queryClient.setQueryData(queryKey, confirmedMode);
            return {
              mode: "confirmed-after-duplicate",
              result: { status: "ok", response: { type: "default" } },
            };
          }
        }

        throw new Error(errorMessage);
      }

      return {
        mode: "enabled",
        result,
      };
    },
    onSuccess: (outcome) => {
      if (outcome.mode === "enabled") {
        queryClient.setQueryData(queryKey, "unifiedAccount");
        onEnableSuccess?.();
      }
    },
    onError: (error) => {
      onEnableError?.(error as Error);
    },
  });

  const enableUnifiedAccount = useCallback(async () => {
    if (!addressKey) {
      throw new Error("Wallet not connected");
    }

    const existingEnablePromise = unifiedAccountEnablePromises.get(addressKey);
    if (existingEnablePromise) {
      await existingEnablePromise;
      return;
    }

    const enablePromise = enableUnifiedAccountMutation().finally(() => {
      if (unifiedAccountEnablePromises.get(addressKey) === enablePromise) {
        unifiedAccountEnablePromises.delete(addressKey);
      }
    });

    unifiedAccountEnablePromises.set(addressKey, enablePromise);
    await enablePromise;
  }, [addressKey, enableUnifiedAccountMutation]);

  return {
    ...readState,
    enableUnifiedAccount,
    isEnabling,
  };
}
